/**
 * SIL-1348 - the technical-details and commercial-terms tile grids on the buyer
 * listing detail (spec section 4.2, handoff section 2 items 3 and 4).
 *
 * ## Absent is not empty
 *
 * `ListingTechnicalDetails` OMITS the key for an attribute the SKU does not
 * carry — SIL-1345 made every field optional and the parser never writes a key
 * it could not resolve. That is the whole mechanism for deciding what to
 * render: `technicalTiles` walks a fixed display ORDER and keeps a key only
 * when it is actually present, so a SKU with three attributes renders three
 * tiles, not eight with five blanks. A `null`-tolerant implementation here
 * would silently re-introduce the empty tiles the omission exists to prevent,
 * which is why the guard is `!== undefined` on a value read from an optional
 * key, never a truthiness check (a legitimate `0` must not vanish) and never a
 * `?? '—'` fallback.
 *
 * When nothing resolves, the section itself renders nothing rather than an
 * empty card with a heading.
 *
 * ## SIL-1377 - and an absent BLOCK is "absent" too
 *
 * An API one version behind sends no `technicalDetails` object and no `evidence`
 * block at all, and both builders below dereferenced them unguarded - which is a
 * white screen on the listing detail for exactly the same reason it was one on
 * the marketplace. Every such input is now optional at the type level, and the
 * degradation is the one this file already implements: a tile with no source is
 * not rendered, and a section with no tiles is not rendered. Nothing is
 * substituted, so the location tile cannot appear without a location and the
 * exact-price tiles cannot appear without a pricing block.
 */
import type {
  AskPricing,
  ListingTechnicalDetails,
  UnverifiedEvidence,
  UnverifiedPricing,
  VerifiedEvidence,
  VerifiedPricing,
} from '../../dto/marketplace.js';
import { Card } from '../Card.js';
import { PanelTitle } from '../PanelTitle.js';
import { TECHNICAL_DETAIL_LABELS } from '../../listing-technical-details.js';
import { listingPriceView } from './listingPriceView.js';
import {
  COMMERCIAL_TERMS_TITLE,
  COMMERCIAL_TERM_LABELS,
  PAYMENT_TERMS_LABELS,
  SOLD_AS_LABELS,
  TECHNICAL_DETAILS_TITLE,
  gigabytesValue,
  gpusPerNodeValue,
  shipsWithinValue,
} from './copy/listingDetail';

export interface Tile {
  label: string;
  value: string;
}

function TileGrid({ title, tiles }: { title: string; tiles: Tile[] }) {
  if (tiles.length === 0) return null;
  return (
    <section>
      <PanelTitle title={title} className="mb-3" />
      <Card>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
          {tiles.map((tile) => (
            <div key={tile.label}>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {tile.label}
              </dt>
              <dd className="mt-1 text-sm text-foreground">{tile.value}</dd>
            </div>
          ))}
        </dl>
      </Card>
    </section>
  );
}

/**
 * Display order for the technical tiles, matching the handoff. Exported for the
 * test that proves a missing attribute produces no tile: the test can walk the
 * same order rather than restating it.
 */
export const TECHNICAL_DETAIL_ORDER = [
  'gpuModel',
  'architecture',
  'gpusPerNode',
  'serverModel',
  'cpu',
  'systemMemoryGb',
  'networking',
  'gpuMemoryGb',
] as const satisfies readonly (keyof ListingTechnicalDetails)[];

function technicalValue(key: keyof ListingTechnicalDetails, value: string | number): string {
  if (key === 'gpusPerNode') return gpusPerNodeValue(value as number);
  if (key === 'systemMemoryGb' || key === 'gpuMemoryGb') return gigabytesValue(value as number);
  return String(value);
}

export function technicalTiles(details: ListingTechnicalDetails | undefined): Tile[] {
  const tiles: Tile[] = [];
  if (details === undefined) return tiles;
  for (const key of TECHNICAL_DETAIL_ORDER) {
    const value = details[key];
    // `!== undefined`, not truthiness: an absent key is the hide signal, and a
    // resolved 0 is a fact worth a tile.
    if (value === undefined) continue;
    tiles.push({ label: TECHNICAL_DETAIL_LABELS[key], value: technicalValue(key, value) });
  }
  return tiles;
}

export function TechnicalDetailsTiles({ details }: { details: ListingTechnicalDetails | undefined }) {
  return <TileGrid title={TECHNICAL_DETAILS_TITLE} tiles={technicalTiles(details)} />;
}

// ---------------------------------------------------------------------------
// Commercial terms
// ---------------------------------------------------------------------------

export interface CommercialTermsInput {
  /** SIL-1377 - `undefined` from an API that sent no pricing block: no price tiles. */
  pricing: VerifiedPricing | UnverifiedPricing | AskPricing | undefined;
  /** SIL-1377 - `undefined` from an API that sent no evidence block: no location tile. */
  evidence: VerifiedEvidence | UnverifiedEvidence | undefined;
  listingType: string;
  /** SIL-1377 - `undefined` (absent key) is treated exactly as `null` (not stated). */
  shipsWithinDays: number | null | undefined;
  /** SIL-1377 - as `shipsWithinDays`. */
  paymentTerms: keyof typeof PAYMENT_TERMS_LABELS | null | undefined;
}

/**
 * Price tiles are the SERVER's numbers formatted, never computed here: exact
 * values for a verified caller, the bucketed ranges for an unverified one
 * (`formatPriceRange`, SIL-1328). Location follows the same rule — a verified
 * caller's payload carries the site, an unverified one's carries the region and
 * no site at all, so there is nothing to suppress client-side.
 */
export function commercialTiles(input: CommercialTermsInput): Tile[] {
  const { pricing, evidence, listingType, shipsWithinDays, paymentTerms } = input;
  const tiles: Tile[] = [];

  // SIL-1377: no pricing block, no price tiles. Omitting them is the only option
  // that is not a claim - a placeholder would read as "this listing has no price".
  if (pricing !== undefined) {
    const view = listingPriceView(pricing, evidence);
    tiles.push({ label: COMMERCIAL_TERM_LABELS.pricePerGpu, value: view.perGpu });
    tiles.push({ label: COMMERCIAL_TERM_LABELS.pricePerNode, value: view.perNode });
  }

  const soldAs = SOLD_AS_LABELS[listingType];
  if (soldAs !== undefined) tiles.push({ label: COMMERCIAL_TERM_LABELS.soldAs, value: soldAs });

  // SIL-1377: `?? null` collapses an absent block into the same "no location to
  // show" the existing `null` case already handled.
  const location =
    evidence === undefined
      ? null
      : evidence.tier === 'verified'
        ? evidence.site ?? evidence.region
        : evidence.region;
  if (location !== null) tiles.push({ label: COMMERCIAL_TERM_LABELS.location, value: location });

  // SIL-1377: `!= null`, not `!== null` - the strict form is TRUE for `undefined`,
  // which is how a skewed payload produced "Ships within undefined days".
  if (shipsWithinDays != null) {
    tiles.push({ label: COMMERCIAL_TERM_LABELS.ships, value: shipsWithinValue(shipsWithinDays) });
  }
  if (paymentTerms != null) {
    tiles.push({ label: COMMERCIAL_TERM_LABELS.payment, value: PAYMENT_TERMS_LABELS[paymentTerms] });
  }

  return tiles;
}

export function CommercialTermsTiles(props: CommercialTermsInput) {
  return <TileGrid title={COMMERCIAL_TERMS_TITLE} tiles={commercialTiles(props)} />;
}
